import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/site/PageHeader";
import SectionHeader from "@/components/site/SectionHeader";
import ContactForm from "@/components/site/ContactForm";
import InquiryForm from "@/components/site/InquiryForm";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/brand";

export default function ContactPage() {
  const [params] = useSearchParams();
  const intent = params.get("intent");
  const defaultTab = intent === "hire" ? "hire" : "general";

  const whatsappHref = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    "Hello RK Enterprises, I would like to know more about your services."
  )}`;

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Let's build your workforce together"
        description="Talk to our team about manpower deployments, applications, or partnerships."
        image={IMAGES.corporateMeeting}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-4 space-y-4">
              <ContactInfoCard
                icon={<MapPin className="h-5 w-5" />}
                title="Visit us"
                lines={[BRAND.address.line1, BRAND.address.line2]}
                testid="contact-info-address"
              />
              <ContactInfoCard
                icon={<Phone className="h-5 w-5" />}
                title="Call us"
                lines={BRAND.phones}
                links={BRAND.phones.map((p) => `tel:${p.replace(/\s/g, "")}`)}
                testid="contact-info-phone"
              />
              <ContactInfoCard
                icon={<Mail className="h-5 w-5" />}
                title="Email us"
                lines={BRAND.emails}
                links={BRAND.emails.map((e) => `mailto:${e}`)}
                testid="contact-info-email"
              />
              <ContactInfoCard
                icon={<Clock className="h-5 w-5" />}
                title="Business hours"
                lines={[BRAND.hours]}
                testid="contact-info-hours"
              />
              <Card className="p-6 bg-[#1F6B5C] text-white border-0 rounded-sm">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 grid place-items-center rounded-sm bg-white/15 shrink-0">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-lg">
                      Quick connect on WhatsApp
                    </h4>
                    <p className="text-sm text-white/85 mt-1">
                      Get a fast response on staffing requirements or job
                      applications.
                    </p>
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-button"
                      className="mt-4 inline-flex items-center gap-2 bg-white text-[#1F6B5C] hover:bg-slate-100 px-4 py-2 rounded-sm font-semibold text-sm transition-colors"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
              </Card>
            </div>

            {/* Forms */}
            <div className="lg:col-span-8">
              <SectionHeader
                eyebrow="Send a message"
                title="Choose the right form for your need"
                description="General inquiries or workforce hiring requests — both reach the right team instantly."
                testid="contact-forms-header"
              />
              <div className="mt-8 p-6 sm:p-8 bg-white border border-[#E2E8F0] rounded-sm shadow-sm">
                <Tabs defaultValue={defaultTab} className="w-full">
                  <TabsList
                    className="grid w-full grid-cols-2 bg-[#F8FAFC] rounded-sm h-12 p-1"
                    data-testid="contact-tabs-list"
                  >
                    <TabsTrigger
                      value="general"
                      className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#1E3A8A] data-[state=active]:shadow-sm"
                      data-testid="contact-tab-general"
                    >
                      General Inquiry
                    </TabsTrigger>
                    <TabsTrigger
                      value="hire"
                      className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#1E3A8A] data-[state=active]:shadow-sm"
                      data-testid="contact-tab-hire"
                    >
                      Hire Workforce
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="general" className="mt-6">
                    <ContactForm />
                  </TabsContent>
                  <TabsContent value="hire" className="mt-6">
                    <InquiryForm />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section
        className="bg-[#F8FAFC] py-16 md:py-24"
        data-testid="contact-map-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Location"
            title="Find us in Avadi, Chennai"
            description={BRAND.address.full}
            align="center"
            testid="contact-map-header"
          />
          <div className="mt-10 rounded-sm overflow-hidden border border-[#E2E8F0] shadow-sm">
            <iframe
              title="RK Enterprises Location"
              data-testid="contact-map-iframe"
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                BRAND.address.full
              )}&output=embed`}
              width="100%"
              height="450"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactInfoCard({ icon, title, lines, links, testid }) {
  return (
    <Card
      data-testid={testid}
      className="rk-lift p-5 bg-white border border-[#E2E8F0] rounded-sm"
    >
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#1E3A8A]/8 text-[#1E3A8A] shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold">
            {title}
          </div>
          <div className="mt-1 space-y-0.5">
            {lines.map((line, i) =>
              links?.[i] ? (
                <a
                  key={line}
                  href={links[i]}
                  className="block text-sm text-[#0F172A] hover:text-[#1E3A8A] break-all"
                >
                  {line}
                </a>
              ) : (
                <div key={line} className="text-sm text-[#0F172A]">
                  {line}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
